import { useParams, Link } from 'react-router-dom';
import { getVideoById, videos } from '@/mocks/videos';
import { categoriesMap } from '@/mocks/categories';
import VideoCard from '@/components/base/VideoCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;
  const categoryName = video ? (categoriesMap[video.category]?.name || video.category) : '';
  const relatedVideos = videos
    .filter((v) => v.id !== id && v.category === video?.category)
    .slice(0, 4);

  const formatViews = (v: number): string => {
    if (v >= 10000) return (v / 10000).toFixed(1) + '万次播放';
    return v + '次播放';
  };

  if (!video) {
    return (
      <div className="pt-24 pb-12 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-800 mb-2">视频未找到</h2>
          <p className="text-foreground-500 mb-6">该视频不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 pb-12 md:pb-16 min-h-screen">
      <article className="w-full max-w-5xl mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <Link to={`/category/${video.category}`} className="hover:text-primary-600 transition-colors">
            {categoryName}
          </Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-800 font-medium line-clamp-1">{video.title}</span>
        </nav>

        <div className="relative aspect-video rounded-xl overflow-hidden mb-6 bg-foreground-950">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <button className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 hover:bg-primary-600 flex items-center justify-center transition-all hover:scale-110 cursor-pointer">
              <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
            </button>
          </div>
          <span className="absolute bottom-4 right-4 px-3 py-1 text-sm font-medium bg-black/70 text-white rounded-md">
            {video.duration}
          </span>
        </div>

        <div className="mb-8">
          <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
            {video.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i> {formatViews(video.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i> {video.rating} 分
            </span>
            <span>{video.date}</span>
            <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
              {categoryName}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {video.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="prose prose-sm max-w-none mb-10">
          <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">视频简介</h2>
          <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
        </div>

        {relatedVideos.length > 0 && (
          <section className="border-t border-background-200/70 pt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedVideos.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}